import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import ImageCarousel from '../components/ImageCarousel';
import BannersCarousel from '../components/BannersCarousel';
import ResponsiveImage from '../components/ResponsiveImage';
import { motion } from 'framer-motion';
import { Settings, Gauge, Power, Ruler, Maximize, ArrowRight, ChevronRight, Wind, Zap, Activity } from 'lucide-react';
import CatalogueModal from '../components/CatalogueModal';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const BJ40Pro = () => {
    const { language } = useLanguage();
    const t = translations[language].models.bj40Pro;
    const [isCatalogueOpen, setIsCatalogueOpen] = useState(false);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-slate-900 text-white font-sans selection:bg-red-600 overflow-x-hidden mt-20 lg:mt-[120px]">
            <SEO
                title="BAIC BJ40 Pro Off-Road SUV | Tough 4x4 Adventure in Oman"
                description="Meet the BAIC BJ40 Pro: rugged 4x4 off-road SUV with enhanced performance, all-terrain capability and modern tech built for Oman."
                canonicalUrl="https://baicoman.com/off-road/bj40-pro"
            />
            <h1 className="sr-only">BAIC BJ40 Pro Off-Road SUV</h1>

            <BannersCarousel
                banners={[
                    {
                        image: "/assets/images/bj40-pro/bj40-pro-banner1.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600 inline-block">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    },
                    {
                        image: "/assets/images/bj40-pro/bj40pro2.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600 inline-block">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    },
                    {
                        image: "/assets/images/bj40-pro/bj40-pro-interior.jpg",
                        title: (
                            <>
                                {t.hero.title.split(' ')[0]} <span className="text-red-600 inline-block">{t.hero.title.split(' ').slice(1).join(' ')}</span>
                            </>
                        ),
                        subtitle: t.hero.subtitle
                    }
                ]}
                use1080={true}
            />

            {/* 2. Exterior Highlights - Zig-Zag Layout */}
            <section className="relative w-full py-20 bg-gray-50 text-slate-900 overflow-hidden">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-20"
                    >
                        <h2 className="text-4xl md:text-7xl font-black mb-4 uppercase tracking-tighter">{t.sections.design.title}</h2>

                        <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                            {language === "ar" ? (
                                <span dir="rtl" className="block text-gray-700">
                                    تصميم خارجي قوي ومتين، صُمّم بعناية ليتحمل أقسى التضاريس وظروف القيادة الوعرة
                                </span>
                            ) : (
                                "A robust exterior crafted for the toughest terrains."
                            )}
                        </p>

                    </motion.div>

                    {/* Row 1 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="order-2 lg:order-1 space-y-6 lg:pr-12"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.design.classic.title.split(' ')[0]} <span className="text-red-600">{t.sections.design.classic.title.split(' ').slice(1).join(' ')}</span></h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.design.classic.desc}
                            </p>
                        </motion.div>
                        <div className="order-1 lg:order-2 w-full">
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/classic-heritage.png"
                                imageAlt="Exterior Front"
                                className="rounded-2xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="w-full">
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/all-terrain-stance.jpeg"
                                imageAlt="Exterior Side"
                                className="rounded-2xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-6 lg:pl-12"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.design.stance.title.split(' ').slice(0, -1).join(' ')} <span className="text-red-600">{t.sections.design.stance.title.split(' ').slice(-1)}</span>
                            </h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.design.stance.desc}
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 2.5. Split Tail Door - New Section */}
            <section className="relative w-full bg-white text-slate-900 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="order-2 lg:order-1 p-12 lg:p-24 space-y-6 bg-gray-50 h-full flex flex-col justify-center"
                    >
                        <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.splitTailDoor.title}</h3>
                        <p className="text-lg text-gray-600 leading-relaxed text-justify">
                            {t.sections.splitTailDoor.desc}
                        </p>
                    </motion.div>
                    <div className="order-1 lg:order-2 w-full h-full min-h-[400px]">
                        <ResponsiveImage
                            image="/assets/images/bj40-pro/split-tail-door.webp"
                            imageAlt="Split Tail Door"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* 3. Performance & Capability - Side-by-Side */}
            <section className="bg-slate-950 py-24 text-white overflow-hidden">
                <div className="container mx-auto px-6 mb-16 text-center">
                    <motion.h2
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl md:text-8xl font-black mb-4 uppercase tracking-tighter"
                    >
                        {t.sections.power.title}
                    </motion.h2>
                </div>

                <div className="w-full px-6 grid grid-cols-1 md:grid-cols-3 gap-8">

                    {/* Feature Block 1 — 1/3 */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="relative group overflow-hidden rounded-3xl h-[600px] border border-gray-800 md:col-span-1"
                    >
                        <ResponsiveImage
                            image="/assets/images/bj40-pro/turbocharged.jpg"
                            imageAlt="Offroad Action"
                            className="h-full"
                            imgClassName="transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-100"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-10 bg-gradient-to-t from-black via-black/80 to-transparent">
                            <Gauge className="w-12 h-12 text-red-600 mb-4" />
                            <h3 className="text-3xl md:text-4xl font-bold mb-2">
                                {t.sections.power.engine.title}
                            </h3>
                            <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                                {t.sections.power.engine.desc}
                            </p>
                        </div>
                    </motion.div>

                    {/* Feature Block 2 — 2/3 */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative group overflow-hidden rounded-3xl h-[600px] border border-gray-800 md:col-span-2"
                    >
                        <ResponsiveImage
                            image="/assets/images/bj40-pro/professional-chasis.png"
                            imageAlt="Suspension"
                            className="h-full"
                            imgClassName="transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-100"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-10 bg-gradient-to-t from-black via-black/80 to-transparent text-right">
                            <div className="flex justify-end mb-4">
                                <Activity className="w-12 h-12 text-red-600" />
                            </div>
                            <h3 className="text-3xl md:text-4xl font-bold mb-2">
                                {t.sections.power.chassis.title}
                            </h3>
                            <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                                {t.sections.power.chassis.desc}
                            </p>
                        </div>
                    </motion.div>

                </div>

            </section>

            {/* 4. Interior Luxury - Immersive overlap */}
            <section className="bg-zinc-900 py-32 text-white overflow-hidden relative">
                {/* Background decorative elements */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-900/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[100px]" />

                <div className="container mx-auto px-6 mb-20 text-center relative z-10">
                    <span className="text-red-500 font-bold uppercase tracking-[0.3em] text-sm md:text-base border-b border-red-500 pb-2">{t.sections.interior.tag}</span>
                    <h2 className="text-5xl md:text-7xl font-black mt-8 tracking-tighter">{t.sections.interior.title}</h2>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid grid-cols-1  gap-8 items-center">
                        {/* Interior 1 - Spans 8 cols */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="lg:col-span-8 relative z-10"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                                <ResponsiveImage
                                    image="/assets/images/bj40-pro/technology-comfort.jpg"
                                    imageAlt="Dashboard"
                                    className="aspect-[16/9]"
                                />
                            </div>
                        </motion.div>

                        {/* Interior 2 - Spans 5 cols, Overlaps, pushed down */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="lg:col-span-5 lg:-ml-24 lg:mt-32 relative z-20"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-zinc-800 group">
                                <ResponsiveImage
                                    image="/assets/images/bj40-pro/bj40-pro-image6.jpg"
                                    imageAlt="Seats"
                                    className="aspect-[4/3]"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 4.5 New Features Section - Zig Zag */}
            <section className="bg-white text-slate-900 py-24 overflow-hidden">
                <div className="container mx-auto px-6 space-y-24">
                    {/* Feature 1: Part-time 4WD System (Text Left, Image Right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-6"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.partTime4WD.title}</h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.partTime4WD.desc}
                            </p>
                        </motion.div>
                        <div>
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/part-time-4wd.png"
                                imageAlt="Part-time 4WD"
                                className="rounded-3xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    </div>

                    {/* Feature 2: Cage Body (Image Left, Text Right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1">
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/professional-chasis.png"
                                imageAlt="Cage Body"
                                className="rounded-3xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="order-1 lg:order-2 space-y-6"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.cageBody.title}</h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.cageBody.desc}
                            </p>
                        </motion.div>
                    </div>

                    {/* Feature 3: Technology Trim (Text Left, Image Right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-6"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.techTrim.title}</h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.techTrim.desc}
                            </p>
                        </motion.div>
                        <div>
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/technology-trim.png"
                                imageAlt="Technology Trim"
                                className="rounded-3xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    </div>

                    {/* Feature 4: Skylight (Image Left, Text Right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1">
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/large-pano.png"
                                imageAlt="Skylight"
                                className="rounded-3xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="order-1 lg:order-2 space-y-6"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.skylight.title}</h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.skylight.desc}
                            </p>
                        </motion.div>
                    </div>

                    {/* Feature 5: Trunk Space (Text Left, Image Right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-6"
                        >
                            <h3 className="text-3xl md:text-5xl font-bold uppercase">{t.sections.trunkSpace.title}</h3>
                            <p className="text-lg text-gray-600 leading-relaxed text-justify">
                                {t.sections.trunkSpace.desc}
                            </p>
                        </motion.div>
                        <div>
                            <ResponsiveImage
                                image="/assets/images/bj40-pro/large-trunk-space.jpeg"
                                imageAlt="Trunk Space"
                                className="rounded-3xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. Gallery - Modern Grid */}
            <section className="bg-white py-24">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-6xl font-black uppercase text-slate-900">{t.sections.gallery.title}</h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            "/assets/images/bj40-pro/bj40-pro-image7.jpg",
                            "/assets/images/bj40-pro/bj40-pro-image8.jpg",
                            "/assets/images/bj40-pro/bj40-pro-image9.jpg"
                        ].map((img, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                className="group overflow-hidden rounded-2xl shadow-lg cursor-pointer"
                            >
                                <ResponsiveImage
                                    image={img}
                                    imageAlt={`Lifestyle ${idx + 1}`}
                                    className="aspect-[3/4] md:aspect-[4/5]"
                                    imgClassName="transition-transform duration-700 group-hover:scale-110"
                                />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 6. Specifications - Tech Cards */}
            <section className="bg-slate-950 py-24 text-white relative overflow-hidden">
                {/* Background Grid Pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <span className="text-red-600 font-bold tracking-widest uppercase">{t.sections.specs.tag}</span>
                        <h2 className="text-4xl md:text-6xl font-black mt-4">{t.sections.specs.title}</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Power, ...t.specs[0] },
                            { icon: Settings, ...t.specs[1] },
                            { icon: Zap, ...t.specs[2] },
                            { icon: Gauge, ...t.specs[3] },
                            { icon: Activity, ...t.specs[4] },
                            { icon: Maximize, ...t.specs[5] },
                            { icon: Ruler, ...t.specs[6] },
                            { icon: Wind, ...t.specs[7] }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors group"
                            >
                                <item.icon className="w-10 h-10 text-red-600 mb-6 group-hover:scale-110 transition-transform" />
                                <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2">{item.label}</h3>
                                <div className="text-2xl font-bold text-white mb-1">{item.value}</div>
                                <div className="text-gray-500 text-sm">{item.sub}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="py-32 bg-red-600 text-white text-center">
                <h2 className="text-6xl font-black mb-8 uppercase">{t.sections.footer.title}</h2>
                <p className="text-2xl mb-12 opacity-90 max-w-2xl mx-auto">{t.sections.footer.desc}</p>
                <div className="flex gap-6 justify-center flex-wrap">
                    <Link to="/test-drive">
                        <Button className="bg-white text-red-600 hover:bg-gray-100 rounded-full px-12 py-8 text-xl font-bold shadow-2xl">
                            {t.sections.footer.btn}
                        </Button>
                    </Link>
                </div>
            </section>

            <CatalogueModal
                isOpen={isCatalogueOpen}
                onClose={() => setIsCatalogueOpen(false)}
                modelName="BJ40 PRO"
            />
        </div>
    );
};

export default BJ40Pro;
